import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpen, FileText, Upload, Download, Eye, Trash2, Edit2,
  MoreVertical, ChevronRight, ChevronDown, Plus, Search,
  Grid3X3, List, SortAsc, SortDesc, Lock, Unlock, MessageSquare,
  HelpCircle, CheckCircle, Clock, Send, X, Loader2, ArrowLeft,
  File, Image, Video, FileSpreadsheet, Presentation, FolderPlus,
  Settings, Users, AtSign, Calendar
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const getFileIcon = (mimeType, filename) => {
  if (!mimeType && filename) {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return Image;
    if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return Video;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
    if (['ppt', 'pptx'].includes(ext)) return Presentation;
    if (['pdf'].includes(ext)) return FileText;
    return File;
  }

  if (mimeType?.startsWith('image/')) return Image;
  if (mimeType?.startsWith('video/')) return Video;
  if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return FileSpreadsheet;
  if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return Presentation;
  if (mimeType?.includes('pdf')) return FileText;
  return File;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// =============================================================================
// DOCUMENTS TAB MAIN COMPONENT
// =============================================================================

const DocumentsTab = ({ userRole, currentOrganizationId, committeeId = null, canManageDocuments = null }) => {
  const navigate = useNavigate();
  const isAdmin = ['owner', 'admin'].includes(userRole);
  const canUploadDocuments = ['owner', 'admin', 'board_member'].includes(userRole);
  const canManage = typeof canManageDocuments === 'boolean' ? canManageDocuments : canUploadDocuments;

  // State
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // list, grid
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [hasExecutiveAccess, setHasExecutiveAccess] = useState(false);
  const [folderOffset, setFolderOffset] = useState(0);
  const [folderTotal, setFolderTotal] = useState(0);
  const [documentOffset, setDocumentOffset] = useState(0);
  const [documentTotal, setDocumentTotal] = useState(0);

  const folderLimit = 50;
  const documentLimit = 50;

  const documentsRef = useRef([]);

  // Dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [documentToMove, setDocumentToMove] = useState(null);
  const [editingFolder, setEditingFolder] = useState(null);
  const [showMoveFolderDialog, setShowMoveFolderDialog] = useState(false);
  const [folderToMove, setFolderToMove] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);

  const currentFolderId = currentFolder?.id || null;
  const currentFolderPath = useMemo(() => {
    const path = [...(folderPath || []), currentFolder].filter(Boolean);
    return path.map(folder => ({ id: folder.id, name: folder.name }));
  }, [folderPath, currentFolder]);

  // Handler for moving documents
  const handleMoveDocument = (doc) => {
    setDocumentToMove(doc);
    setShowMoveDialog(true);
  };

  const handleDocumentMoved = useCallback((documentId, targetFolderId) => {
    setDocuments(prev => {
      const exists = prev.find(d => d.id === documentId);
      if (!exists) return prev;
      if ((targetFolderId || null) !== (currentFolderId || null)) {
        return prev.filter(d => d.id !== documentId);
      }
      return prev.map(d => d.id === documentId ? { ...d, folder_id: targetFolderId || null } : d);
    });
  }, [currentFolder]);

  const handleMoveFolder = (folder) => {
    setFolderToMove(folder);
    setShowMoveFolderDialog(true);
  };

  // Fetch data
  const fetchFolders = useCallback(async ({ append = false, nextOffset = 0 } = {}) => {
    try {
      const folderId = currentFolder?.id || null;
      const params = new URLSearchParams();
      if (folderId) params.append('parent_folder_id', folderId);
      if (committeeId) params.append('committee_id', committeeId);
      params.append('limit', folderLimit);
      params.append('offset', nextOffset);

      const foldersRes = await api.get(`/board/documents/folders?${params.toString()}`);
      const newFolders = foldersRes.data.folders || [];
      setFolders(prev => (append ? [...prev, ...newFolders] : newFolders));
      setFolderOffset(nextOffset);
      setFolderTotal(foldersRes.data.total ?? newFolders.length + nextOffset);
      setHasExecutiveAccess(foldersRes.data.has_executive_access);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      toast.error('Failed to load documents');
    }
  }, [currentFolder, folderLimit, currentOrganizationId, committeeId]);

  const fetchDocuments = useCallback(async ({ append = false, nextOffset = 0 } = {}) => {
    try {
      const folderId = currentFolder?.id || null;
      const docsParams = new URLSearchParams();
      if (folderId) docsParams.append('folder_id', folderId);
      if (committeeId) docsParams.append('committee_id', committeeId);
      docsParams.append('sort_by', sortBy);
      docsParams.append('sort_order', sortOrder);
      docsParams.append('limit', documentLimit);
      docsParams.append('offset', nextOffset);

      const docsRes = await api.get(`/board/documents?${docsParams.toString()}`);
      const newDocs = docsRes.data.documents || [];
      setDocuments(prev => (append ? [...prev, ...newDocs] : newDocs));
      setDocumentOffset(nextOffset);
      setDocumentTotal(docsRes.data.total ?? newDocs.length + nextOffset);
      setHasExecutiveAccess(prev => prev || docsRes.data.has_executive_access);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      toast.error('Failed to load documents');
    }
  }, [currentFolder, sortBy, sortOrder, documentLimit, currentOrganizationId, committeeId]);

  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  useEffect(() => {
    setLoading(true);
    setFolderOffset(0);
    setDocumentOffset(0);
    Promise.all([
      fetchFolders({ append: false, nextOffset: 0 }),
      fetchDocuments({ append: false, nextOffset: 0 })
    ]).finally(() => setLoading(false));
  }, [fetchFolders, fetchDocuments]);

  useEffect(() => {
    setFolders([]);
    setDocuments([]);
    setCurrentFolder(null);
    setFolderPath([]);
    setSearchQuery('');
    setFolderOffset(0);
    setDocumentOffset(0);
    setFolderTotal(0);
    setDocumentTotal(0);
    setLoading(true);
    setHasExecutiveAccess(false);
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[Governance] org switch reset', { currentOrganizationId });
    }
  }, [currentOrganizationId, committeeId]);

  const loadMoreFolders = () => {
    fetchFolders({ append: true, nextOffset: folderOffset + folderLimit });
  };

  const loadMoreDocuments = () => {
    fetchDocuments({ append: true, nextOffset: documentOffset + documentLimit });
  };

  const refreshData = () => {
    fetchFolders({ append: false, nextOffset: 0 });
    fetchDocuments({ append: false, nextOffset: 0 });
  };

  const shouldShowInCurrentFolder = useCallback((folderId) => {
    const currentFolderId = currentFolder?.id || null;
    return (folderId || null) === currentFolderId;
  }, [currentFolder]);

  const handleDocumentUploaded = useCallback((doc) => {
    if (!doc || !shouldShowInCurrentFolder(doc.folder_id)) return;
    setDocuments((prev) => {
      const exists = prev.some(item => item.id === doc.id);
      if (exists) return prev;
      setDocumentTotal(total => total + 1);
      return [doc, ...prev];
    });
  }, [shouldShowInCurrentFolder]);

  const refetchDocumentsDefensive = useCallback(async (expectedIds) => {
    if (!expectedIds || expectedIds.length === 0) return;
    const folderId = currentFolder?.id || null;
    const maxRetries = 2;
    const delayMs = 400;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const docsParams = new URLSearchParams();
        if (folderId) docsParams.append('folder_id', folderId);
        if (committeeId) docsParams.append('committee_id', committeeId);
        docsParams.append('sort_by', sortBy);
        docsParams.append('sort_order', sortOrder);
        docsParams.append('limit', documentLimit);
        docsParams.append('offset', 0);

        const docsRes = await api.get(`/board/documents?${docsParams.toString()}`);
        const freshDocs = docsRes.data.documents || [];
        const existingDocs = documentsRef.current || [];
        let mergedDocs = freshDocs;

        if (documentOffset > 0 && existingDocs.length > documentLimit) {
          const seen = new Set(freshDocs.map(doc => doc.id));
          const rest = existingDocs.slice(documentLimit).filter(doc => !seen.has(doc.id));
          mergedDocs = [...freshDocs, ...rest];
        }

        setDocuments(mergedDocs);
        if (typeof docsRes.data.total === 'number') {
          setDocumentTotal(docsRes.data.total);
        }
        if (docsRes.data.has_executive_access) {
          setHasExecutiveAccess(prev => prev || docsRes.data.has_executive_access);
        }

        const missing = expectedIds.some(id => !mergedDocs.some(doc => doc.id === id));
        if (!missing) break;
      } catch (err) {
        console.warn('Defensive refetch failed:', err);
      }

      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }, [currentFolder, sortBy, sortOrder, documentLimit, documentOffset, committeeId]);

  const handleBatchComplete = useCallback(({ successes }) => {
    const relevant = (successes || []).filter(doc => shouldShowInCurrentFolder(doc.folder_id));
    const expectedIds = relevant.map(doc => doc.id).filter(Boolean);
    if (expectedIds.length === 0) return;
    refetchDocumentsDefensive(expectedIds);
  }, [shouldShowInCurrentFolder, refetchDocumentsDefensive]);

  // Navigation
  const navigateToFolder = (folder) => {
    if (folder) {
      setFolderPath([...folderPath, currentFolder].filter(Boolean));
      setCurrentFolder(folder);
    } else {
      setFolderPath([]);
      setCurrentFolder(null);
    }
  };

  const navigateBack = () => {
    const newPath = [...folderPath];
    const parentFolder = newPath.pop();
    setFolderPath(newPath);
    setCurrentFolder(parentFolder || null);
  };

  const navigateToPathIndex = (index) => {
    if (index < 0) {
      setFolderPath([]);
      setCurrentFolder(null);
    } else {
      const newPath = folderPath.slice(0, index);
      setFolderPath(newPath);
      setCurrentFolder(folderPath[index]);
    }
  };

  // Open document viewer - navigate to full page
  const openDocument = (doc) => {
    const base = `/governance/documents/${doc.id}`;
    navigate(committeeId ? `${base}?committee_id=${committeeId}` : base);
  };

  // Filter items by search
  const filteredFolders = folders.filter(f =>
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredDocuments = documents.filter(d =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const hasMoreFolders = folders.length < folderTotal;
  const hasMoreDocuments = documents.length < documentTotal;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="documents-tab">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-1 text-sm">
                <button
                  onClick={() => navigateToPathIndex(-1)}
                  className="text-purple-600 hover:text-purple-800 font-medium"
                >
                  Documents
                </button>
                {folderPath.map((folder, idx) => (
                  <React.Fragment key={folder.id}>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                    <button
                      onClick={() => navigateToPathIndex(idx)}
                      className="text-purple-600 hover:text-purple-800"
                    >
                      {folder.name}
                    </button>
                  </React.Fragment>
                ))}
                {currentFolder && (
                  <>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">{currentFolder.name}</span>
                  </>
                )}
              </nav>
            </div>

            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-48"
                />
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
              </div>

              {/* Sort */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    {sortOrder === 'asc' ? <SortAsc className="h-4 w-4 mr-2" /> : <SortDesc className="h-4 w-4 mr-2" />}
                    {sortBy === 'name' ? 'Name' : sortBy === 'date' ? 'Date' : sortBy === 'size' ? 'Size' : 'Type'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setSortBy('name')}>Name</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy('date')}>Date</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy('size')}>Size</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy('type')}>Type</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
                    {sortOrder === 'asc' ? 'Descending' : 'Ascending'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Actions */}
              {canManage && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0">
                      <Plus className="h-4 w-4 mr-2" />
                      New
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShowUploadDialog(true)}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Document
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowCreateFolderDialog(true)}>
                      <FolderPlus className="h-4 w-4 mr-2" />
                      Create Folder
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 lg:p-6">
          {filteredFolders.length === 0 && filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                {currentFolder ? 'This folder is empty' : 'No Documents Yet'}
              </h4>
              <p className="text-gray-500 max-w-md mx-auto mb-6">
                {currentFolder
                  ? 'Upload documents or create subfolders to organize your files.'
                  : committeeId
                    ? 'Upload committee documents such as meeting materials, reports, and reference files.'
                    : 'Upload board documents such as meeting agendas, minutes, resolutions, and financial reports.'}
              </p>
              {canManage && (
                <div className="flex justify-center gap-3">
                  <Button variant="outline" onClick={() => setShowCreateFolderDialog(true)}>
                    <FolderPlus className="h-4 w-4 mr-2" />
                    Create Folder
                  </Button>
                  <Button
                    onClick={() => setShowUploadDialog(true)}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </Button>
                </div>
              )}
            </div>
          ) : viewMode === 'list' ? (
            <div>
              {/* Column Headers */}
              <div className="grid grid-cols-12 items-center gap-4 px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100">
              <div className="col-span-12 sm:col-span-6 lg:col-span-4">Name</div>
                <div className="hidden sm:block col-span-2">Type / Contents</div>
                <div className="hidden lg:block col-span-1">Size</div>
                <div className="hidden lg:block col-span-2">Owner</div>
                <div className="hidden md:block col-span-2">Modified</div>
                <div className="col-span-1"></div>
              </div>

              {/* Folders */}
              {filteredFolders.map((folder) => (
                <FolderRow
                  key={folder.id}
                  folder={folder}
                  onClick={() => navigateToFolder(folder)}
                  onEdit={canManage ? () => setEditingFolder(folder) : null}
                  onMove={canManage ? () => handleMoveFolder(folder) : null}
                  canManage={canManage}
                  committeeId={committeeId}
                  onRefresh={refreshData}
                />
              ))}

              {/* Documents */}
              {filteredDocuments.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  document={doc}
                  onClick={() => openDocument(doc)}
                  canManage={canManage}
                  committeeId={committeeId}
                  onRefresh={refreshData}
                  onMove={handleMoveDocument}
                  onEditSecurity={setEditingDocument}
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-4">
              {/* Folders */}
              {filteredFolders.map((folder) => (
                <FolderCard
                  key={folder.id}
                  folder={folder}
                  onClick={() => navigateToFolder(folder)}
                  onEdit={canManage ? () => setEditingFolder(folder) : null}
                  onMove={canManage ? () => handleMoveFolder(folder) : null}
                  canManage={canManage}
                  committeeId={committeeId}
                  onRefresh={refreshData}
                />
              ))}

              {/* Documents */}
              {filteredDocuments.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  onClick={() => openDocument(doc)}
                  canManage={canManage}
                  onMove={handleMoveDocument}
                />
              ))}
            </div>
          )}

          {(hasMoreFolders || hasMoreDocuments) && (
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              {hasMoreFolders && (
                <Button variant="outline" onClick={loadMoreFolders}>
                  Load more folders
                </Button>
              )}
              {hasMoreDocuments && (
                <Button variant="outline" onClick={loadMoreDocuments}>
                  Load more documents
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <UploadDocumentDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        currentFolderId={currentFolder?.id}
        currentFolderPath={currentFolderPath}
        committeeId={committeeId}
        canManageSecurity={isAdmin || hasExecutiveAccess}
        onDocumentUploaded={handleDocumentUploaded}
        onBatchComplete={handleBatchComplete}
        onSuccess={() => {
          setShowUploadDialog(false);
        }}
      />

      <CreateFolderDialog
        open={showCreateFolderDialog}
        onOpenChange={setShowCreateFolderDialog}
        parentFolderId={currentFolder?.id}
        committeeId={committeeId}
        onSuccess={() => {
          setShowCreateFolderDialog(false);
          refreshData();
          toast.success('Folder created successfully');
        }}
      />

      <EditFolderDialog
        open={!!editingFolder}
        onOpenChange={(open) => !open && setEditingFolder(null)}
        folder={editingFolder}
        committeeId={committeeId}
        onSuccess={() => {
          setEditingFolder(null);
          refreshData();
          toast.success('Folder updated successfully');
        }}
      />

      <MoveDocumentDialog
        open={showMoveDialog}
        onOpenChange={setShowMoveDialog}
        document={documentToMove}
        committeeId={committeeId}
        onSuccess={({ documentId, folderId } = {}) => {
          if (documentId) {
            handleDocumentMoved(documentId, folderId ?? null);
          }
          setShowMoveDialog(false);
          setDocumentToMove(null);
        }}
      />

      <MoveFolderDialog
        open={showMoveFolderDialog}
        onOpenChange={(open) => {
          setShowMoveFolderDialog(open);
          if (!open) {
            setFolderToMove(null);
          }
        }}
        folder={folderToMove}
        committeeId={committeeId}
        onSuccess={() => {
          setShowMoveFolderDialog(false);
          setFolderToMove(null);
          fetchFolders({ append: false, nextOffset: 0 });
        }}
      />
    </div>
  );
};

// =============================================================================
// FOLDER ROW COMPONENT
// =============================================================================

const FolderRow = ({ folder, onClick, onEdit, onMove, canManage, onRefresh, committeeId }) => {
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${folder.name}"? Documents inside will be moved to Root.`)) return;

    try {
      const url = committeeId
        ? `/board/documents/folders/${folder.id}?committee_id=${committeeId}`
        : `/board/documents/folders/${folder.id}`;
      await api.delete(url);
      toast.success('Folder deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete folder');
    }
  };

  return (
    <div
      onClick={onClick}
      className="grid grid-cols-12 items-center gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer group transition-colors"
    >
      {/* Icon + Name - spans 5 columns on large, more on small */}
      <div className="col-span-11 sm:col-span-6 lg:col-span-4 flex items-center gap-3 min-w-0">
        <div
          className="p-2 rounded-lg flex-shrink-0"
          style={{ backgroundColor: `${folder.color}20` }}
        >
          <FolderOpen className="h-5 w-5" style={{ color: folder.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900 truncate">{folder.name}</p>
            {folder.is_locked && <Lock className="h-3 w-3 text-amber-500 flex-shrink-0" />}
          </div>
          {folder.description && (
            <p className="text-xs text-gray-400 truncate hidden lg:block">{folder.description}</p>
          )}
        </div>
      </div>

      {/* Contents - spans 2 columns */}
      <div className="hidden sm:flex col-span-2 text-sm text-gray-500">
        {folder.subfolder_count > 0 && `${folder.subfolder_count} folders, `}
        {folder.document_count} items
      </div>

      {/* Size placeholder - keeps folder actions aligned with document rows */}
      <div className="hidden lg:block col-span-1" />

      {/* Created by - spans 2 columns */}
      <div className="hidden lg:block col-span-2 text-sm text-gray-400 truncate">
        {folder.created_by}
      </div>

      {/* Date - spans 2 columns */}
      <div className="hidden md:block col-span-2 text-sm text-gray-400">
        {formatDate(folder.created_at)}
      </div>

      {/* Actions - spans 1 column */}
      <div className="col-span-1 flex items-center justify-end gap-2">
        <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit && onEdit(); }}>
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove && onMove(); }}>
                <FolderOpen className="h-4 w-4 mr-2" />
                Move Folder
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDelete} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// DOCUMENT ROW COMPONENT
// =============================================================================

const DocumentRow = ({ document: doc, onClick, canManage, onRefresh, onMove, committeeId, onEditSecurity }) => {
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${doc.name}"?`)) return;

    try {
      const url = committeeId
        ? `/board/documents/${doc.id}?committee_id=${committeeId}`
        : `/board/documents/${doc.id}`;
      await api.delete(url);
      toast.success('Document deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const renderFileIcon = () => {
    const IconComponent = getFileIcon(doc.mime_type, doc.original_filename);
    return <IconComponent className="h-5 w-5 text-purple-600" />;
  };

  return (
    <div
      onClick={onClick}
      className="grid grid-cols-12 items-center gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer group transition-colors"
    >
      {/* Icon + Name - spans 4 columns on large */}
      <div className="col-span-11 sm:col-span-6 lg:col-span-4 flex items-center gap-3 min-w-0">
        <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
          {renderFileIcon()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900 truncate">{doc.name}</p>
            {doc.access_scope === 'executive_only' && <Lock className="h-3 w-3 text-amber-500 flex-shrink-0" />}
            {doc.download_policy === 'preview_only' && <Eye className="h-3 w-3 text-blue-500 flex-shrink-0" />}
          </div>
          <p className="text-xs text-gray-400 truncate sm:hidden">
            {doc.document_type} • {formatFileSize(doc.file_size)}
          </p>
        </div>
      </div>

      {/* Type badge - spans 2 columns */}
      <div className="hidden sm:block col-span-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">
          {doc.document_type}
        </span>
      </div>

      {/* Size - spans 1 column */}
      <div className="hidden lg:block col-span-1 text-sm text-gray-500">
        {formatFileSize(doc.file_size)}
      </div>

      {/* Uploaded by - spans 2 columns */}
      <div className="hidden lg:block col-span-2 text-sm text-gray-400 truncate">
        {doc.uploaded_by}
      </div>

      {/* Date + Activity - spans 2 columns */}
      <div className="hidden md:flex col-span-2 items-center gap-3 text-sm text-gray-400">
        <span>{formatDate(doc.created_at)}</span>
        <div className="flex items-center gap-2">
          {doc.comment_count > 0 && (
            <span className="flex items-center gap-0.5" title="Comments">
              <MessageSquare className="h-3.5 w-3.5" />
              {doc.comment_count}
            </span>
          )}
          {doc.question_count > 0 && (
            <span className="flex items-center gap-0.5" title="Questions">
              <HelpCircle className="h-3.5 w-3.5" />
              {doc.question_count}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="col-span-1 flex items-center justify-end">
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onClick(); }}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove && onMove(doc); }}>
                <FolderOpen className="h-4 w-4 mr-2" />
                Move to Folder
              </DropdownMenuItem>
              {doc.can_change_security && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEditSecurity && onEditSecurity(doc); }}>
                  <Settings className="h-4 w-4 mr-2" />
                  Security
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDelete} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// FOLDER CARD COMPONENT (Grid View)
// =============================================================================

const FolderCard = ({ folder, onClick, onEdit, onMove, canManage, onRefresh, committeeId }) => {
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${folder.name}"? Documents inside will be moved to Root.`)) return;

    try {
      const url = committeeId
        ? `/board/documents/folders/${folder.id}?committee_id=${committeeId}`
        : `/board/documents/folders/${folder.id}`;
      await api.delete(url);
      toast.success('Folder deleted');
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete folder');
    }
  };

  return (
    <div
      onClick={onClick}
      className="p-4 rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-md cursor-pointer transition-all group"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${folder.color}20` }}
        >
          <FolderOpen className="h-6 w-6" style={{ color: folder.color }} />
        </div>
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit && onEdit(); }}>
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove && onMove(); }}>
                <FolderOpen className="h-4 w-4 mr-2" />
                Move Folder
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDelete} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="flex items-center gap-2 mb-1">
        <p className="font-medium text-gray-900 truncate">{folder.name}</p>
        {folder.is_locked && <Lock className="h-3 w-3 text-amber-500" />}
      </div>
      <p className="text-xs text-gray-500">{folder.document_count} documents</p>
    </div>
  );
};

// =============================================================================
// DOCUMENT CARD COMPONENT (Grid View)
// =============================================================================

const DocumentCard = ({ document: doc, onClick, canManage, onMove }) => {
  const renderFileIcon = () => {
    const IconComponent = getFileIcon(doc.mime_type, doc.original_filename);
    return <IconComponent className="h-6 w-6 text-purple-600" />;
  };

  return (
    <div
      onClick={onClick}
      className="p-4 rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-md cursor-pointer transition-all group"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
          {renderFileIcon()}
        </div>
        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onClick(); }}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onMove && onMove(doc); }}>
                <FolderOpen className="h-4 w-4 mr-2" />
                Move to Folder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="flex items-center gap-2 mb-1">
        <p className="font-medium text-gray-900 truncate text-sm">{doc.name}</p>
        {doc.is_locked && <Lock className="h-3 w-3 text-amber-500" />}
      </div>
      <p className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</p>
    </div>
  );
};

// =============================================================================
// FOLDER PICKER (Shared)
// =============================================================================

const FolderPicker = ({
  open,
  selectedFolderId,
  onSelectFolderId,
  excludeFolderIds = [],
  selectedLabel = 'Selected',
  showTree = false,
  blockFolderId = null,
  initialFolderPath = [],
  committeeId = null,
}) => {
  const [folderPath, setFolderPath] = useState([]);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [allFolders, setAllFolders] = useState([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [destinationSearch, setDestinationSearch] = useState('');
  const wasOpenRef = useRef(false);

  const excluded = useRef(new Set(excludeFolderIds || []));

  useEffect(() => {
    excluded.current = new Set(excludeFolderIds || []);
  }, [excludeFolderIds]);

  const fetchFolders = async (parentId, offset = 0, limit = 200) => {
    setLoadingFolders(true);
    try {
      const params = new URLSearchParams();
      if (parentId) params.append('parent_folder_id', parentId);
      params.append('limit', limit);
      params.append('offset', offset);
      if (committeeId) params.append('committee_id', committeeId);
      const url = `/board/documents/folders?${params.toString()}`;
      const response = await api.get(url);
      setAvailableFolders(response.data.folders || []);
    } catch (err) {
      console.error('Failed to fetch folders:', err);
    } finally {
      setLoadingFolders(false);
    }
  };

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const nextPath = initialFolderPath?.length ? initialFolderPath : [];
      setFolderPath(nextPath);
      setDestinationSearch('');
      if (showTree) {
        setAllFolders([]);
      } else {
        const parentId = selectedFolderId || null;
        fetchFolders(parentId);
      }
    }
    wasOpenRef.current = open;
  }, [open, showTree, selectedFolderId, initialFolderPath, committeeId]);

  useEffect(() => {
    if (!open || !showTree) return;
    let isActive = true;

    const fetchAllFolders = async () => {
      setLoadingTree(true);
      try {
        const limit = 200;
        const collected = [];
        const queue = [null];

        let offset = 0;
        while (true) {
          const params = new URLSearchParams();
          params.append('include_all', 'true');
          params.append('limit', limit);
          params.append('offset', offset);
          if (committeeId) params.append('committee_id', committeeId);
          const response = await api.get(`/board/documents/folders?${params.toString()}`);
          const folders = response.data.folders || [];
          collected.push(...folders);
          if (folders.length < limit) break;
          offset += limit;
        }

        if (isActive) {
          setAllFolders(collected);
        }
      } catch (err) {
        console.error('Failed to fetch full folder tree:', err);
      } finally {
        if (isActive) {
          setLoadingTree(false);
        }
      }
    };

    fetchAllFolders();

    return () => {
      isActive = false;
    };
  }, [open, showTree, committeeId]);

  const buildTree = useCallback((folders) => {
    const map = new Map();
    folders.forEach((folder) => {
      map.set(folder.id, { ...folder, children: [] });
    });
    const roots = [];
    map.forEach((node) => {
      if (node.parent_folder_id && map.has(node.parent_folder_id)) {
        map.get(node.parent_folder_id).children.push(node);
      } else {
        roots.push(node);
      }
    });
    const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');
    const sortTree = (nodes) => {
      nodes.sort(sortByName);
      nodes.forEach((node) => sortTree(node.children));
    };
    sortTree(roots);
    return roots;
  }, []);

  const flattenTree = useCallback((nodes, depth = 0, acc = []) => {
    nodes.forEach((node) => {
      acc.push({ node, depth });
      if (node.children?.length) {
        flattenTree(node.children, depth + 1, acc);
      }
    });
    return acc;
  }, []);

  const blocked = useMemo(() => {
    if (!blockFolderId || allFolders.length === 0) {
      return new Set();
    }
    const childMap = new Map();
    allFolders.forEach((folder) => {
      if (folder.parent_folder_id) {
        const list = childMap.get(folder.parent_folder_id) || [];
        list.push(folder.id);
        childMap.set(folder.parent_folder_id, list);
      }
    });
    const result = new Set([blockFolderId]);
    const stack = [blockFolderId];
    while (stack.length > 0) {
      const current = stack.pop();
      const children = childMap.get(current) || [];
      children.forEach((childId) => {
        if (!result.has(childId)) {
          result.add(childId);
          stack.push(childId);
        }
      });
    }
    return result;
  }, [blockFolderId, allFolders]);

  const navigateToFolder = (folder) => {
    setFolderPath((prev) => [...prev, { id: folder.id, name: folder.name }]);
    onSelectFolderId(folder.id);
    fetchFolders(folder.id);
  };

  const navigateBack = () => {
    if (folderPath.length > 0) {
      const newPath = [...folderPath];
      newPath.pop();
      setFolderPath(newPath);
      const parent = newPath.length > 0 ? newPath[newPath.length - 1] : null;
      onSelectFolderId(parent?.id || null);
      fetchFolders(parent?.id || null);
    }
  };

  const navigateToRoot = () => {
    setFolderPath([]);
    onSelectFolderId(null);
    fetchFolders(null);
  };

  const getSelectedFolderName = () => {
    if (!selectedFolderId) return null;
    const source = showTree ? allFolders : availableFolders;
    const folder = source.find(f => f.id === selectedFolderId);
    if (folder) return folder.name;
    const fromPath = folderPath.find(f => f.id === selectedFolderId);
    if (fromPath) return fromPath.name;
    return 'Selected Folder';
  };

  const searchTerm = destinationSearch.trim().toLowerCase();
  const filteredFolders = availableFolders.filter(folder =>
    !excluded.current.has(folder.id) &&
    (!searchTerm || folder.name.toLowerCase().includes(searchTerm))
  );
  const searchableTreeFolders = allFolders.filter(folder =>
    !excluded.current.has(folder.id) &&
    (!searchTerm || folder.name.toLowerCase().includes(searchTerm))
  );
  const treeNodes = showTree ? buildTree(searchableTreeFolders) : [];
  const flatTree = showTree ? flattenTree(treeNodes) : [];

  return (
    <div className="border rounded-lg p-3 bg-gray-50">
      {/* Breadcrumb navigation */}
      <div className="flex items-center gap-1 text-sm mb-3 flex-wrap">
        <button
          onClick={navigateToRoot}
          className="text-purple-600 hover:underline font-medium"
        >
          Documents
        </button>
        {folderPath.map((item, idx) => (
          <span key={item.id} className="flex items-center">
            <ChevronRight className="h-4 w-4 text-gray-400" />
            <button
              onClick={() => {
                const newPath = folderPath.slice(0, idx + 1);
                setFolderPath(newPath);
                onSelectFolderId(item.id);
                fetchFolders(item.id);
              }}
              className="text-purple-600 hover:underline"
            >
              {item.name}
            </button>
          </span>
        ))}
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={destinationSearch}
          onChange={(e) => setDestinationSearch(e.target.value)}
          placeholder={showTree ? "Search all folders..." : "Search folders in this location..."}
          className="pl-9 bg-white"
        />
      </div>

      {/* Selected folder indicator */}
      {selectedFolderId ? (
        <div className="flex items-center gap-2 mb-3 p-2 bg-purple-50 rounded-lg border border-purple-200">
          <FolderOpen className="h-5 w-5 text-purple-600" />
          <span className="text-purple-700 font-medium">{selectedLabel}: {getSelectedFolderName()}</span>
          <CheckCircle className="h-4 w-4 text-green-600 ml-auto" />
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-3 p-2 bg-gray-100 rounded-lg border border-gray-200">
          <FolderOpen className="h-5 w-5 text-gray-500" />
          <span className="text-gray-700 font-medium">{selectedLabel}: Root</span>
          <CheckCircle className="h-4 w-4 text-green-600 ml-auto" />
        </div>
      )}

      {/* Folder list */}
      {(showTree ? loadingTree : loadingFolders) ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
        </div>
      ) : (showTree ? flatTree.length > 0 : filteredFolders.length > 0) ? (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          <div
            className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
              selectedFolderId === null
                ? 'bg-purple-100 border border-purple-300'
                : 'hover:bg-gray-100'
            }`}
            onClick={() => onSelectFolderId(null)}
          >
            <div className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-gray-500" />
              <span className="font-medium text-gray-700">Root</span>
            </div>
            <div className="flex items-center gap-2">
              {selectedFolderId === null && (
                <CheckCircle className="h-4 w-4 text-green-600" />
              )}
            </div>
          </div>
          {showTree ? flatTree.map(({ node, depth }) => {
            const isBlocked = blocked.has(node.id);
            return (
              <div
                key={node.id}
                className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                  isBlocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                } ${
                  selectedFolderId === node.id
                    ? 'bg-purple-100 border border-purple-300'
                    : !isBlocked ? 'hover:bg-gray-100' : ''
                }`}
                onClick={() => {
                  if (!isBlocked) onSelectFolderId(node.id);
                }}
              >
                <div className="flex items-center gap-2" style={{ paddingLeft: 8 + depth * 16 }}>
                  <FolderOpen className="h-5 w-5" style={{ color: node.color || '#6B7280' }} />
                  <span className="font-medium text-gray-700">{node.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  {selectedFolderId === node.id && (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  )}
                </div>
              </div>
            );
          }) : filteredFolders.map(folder => (
            <div
              key={folder.id}
              className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                selectedFolderId === folder.id
                  ? 'bg-purple-100 border border-purple-300'
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => onSelectFolderId(folder.id)}
            >
              <div className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5" style={{ color: folder.color || '#6B7280' }} />
                <span className="font-medium text-gray-700">{folder.name}</span>
                {folder.subfolder_count > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {folder.subfolder_count} subfolders
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectedFolderId === folder.id && (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
                {folder.subfolder_count > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToFolder(folder);
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-sm text-center py-4">
          {destinationSearch.trim()
            ? 'No matching folders found'
            : folderPath.length > 0 ? 'No subfolders in this location' : 'No folders available'}
        </p>
      )}

      {/* Back button */}
      {!showTree && folderPath.length > 0 && (
        <Button variant="outline" size="sm" onClick={navigateBack} className="mt-2">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
      )}
    </div>
  );
};

// =============================================================================
// UPLOAD DOCUMENT DIALOG
// =============================================================================

const UploadDocumentDialog = ({ open, onOpenChange, currentFolderId, currentFolderPath, onSuccess, onDocumentUploaded, onBatchComplete, committeeId, canManageSecurity }) => {
  const [files, setFiles] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState('general');
  const [isLocked, setIsLocked] = useState(false);
  const [accessScope, setAccessScope] = useState(committeeId ? 'committee' : 'board');
  const [downloadPolicy, setDownloadPolicy] = useState('download');
  const [uploading, setUploading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(currentFolderId || null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, currentFile: '' });
  const [uploadSummary, setUploadSummary] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch folders when dialog opens or when navigating
  useEffect(() => {
    if (open) {
      setSelectedFolderId(currentFolderId || null);
      setFiles([]);
      setUploadQueue([]);
      setUploadProgress({ current: 0, total: 0, currentFile: '' });
      setUploadSummary(null);
    }
  }, [open, currentFolderId]);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      setFiles(selectedFiles);
      setUploadQueue([]);
      setUploadSummary(null);
      if (selectedFiles.length === 1) {
        const selectedFile = selectedFiles[0];
        if (!name) {
          setName(selectedFile.name.replace(/\.[^/.]+$/, ''));
        }
      } else {
        setName('');
      }
    }
  };

  const buildUploadDoc = (file, resolvedName, responseId) => ({
    id: responseId,
    name: resolvedName,
    description,
    document_type: documentType,
    file_size: file.size,
    mime_type: file.type,
    original_filename: file.name,
    is_locked: isLocked,
    access_scope: accessScope,
    download_policy: downloadPolicy,
    can_download: downloadPolicy !== 'preview_only',
    folder_id: selectedFolderId || null,
    uploaded_by: 'You',
    comment_count: 0,
    question_count: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  const uploadSingleFile = async (file, resolvedName, index) => {
    setUploadQueue(prev => prev.map((item, idx) => idx === index ? { ...item, status: 'uploading' } : item));
    setUploadProgress({ current: index + 1, total: files.length, currentFile: file.name });
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', resolvedName);
      formData.append('description', description);
      formData.append('document_type', documentType);
      if (canManageSecurity) {
        formData.append('is_locked', isLocked);
        formData.append('access_scope', accessScope);
        formData.append('download_policy', downloadPolicy);
      }
      if (selectedFolderId) {
        formData.append('folder_id', selectedFolderId);
      }

      if (committeeId) {
        formData.append('committee_id', committeeId);
      }
      const response = await api.post('/board/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedDoc = buildUploadDoc(file, resolvedName, response.data?.id);
      onDocumentUploaded && onDocumentUploaded(uploadedDoc);

      setUploadQueue(prev => prev.map((item, idx) => idx === index ? { ...item, status: 'success' } : item));
      return { success: true, document: uploadedDoc };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to upload document';
      setUploadQueue(prev => prev.map((item, idx) => idx === index ? { ...item, status: 'failed', error: message } : item));
      return { success: false, error: message, file };
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    if (files.length === 1 && !name.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setUploading(true);
    setUploadSummary(null);
    try {
      const queue = files.map((file) => ({
        name: files.length === 1 ? name : file.name.replace(/\.[^/.]+$/, ''),
        status: 'pending',
      }));
      setUploadQueue(queue);

      const successes = [];
      const failures = [];

      for (let i = 0; i < files.length; i++) {
        const resolvedName = files.length === 1 ? name : files[i].name.replace(/\.[^/.]+$/, '');
        const result = await uploadSingleFile(files[i], resolvedName, i);
        if (result.success) {
          successes.push(result.document);
        } else {
          failures.push({ file: files[i], error: result.error });
        }
      }

      const summary = {
        total: files.length,
        success: successes.length,
        failed: failures.length,
      };
      setUploadSummary(summary);
      onBatchComplete && onBatchComplete({ successes, failures });

      if (summary.failed === 0) {
        toast.success(`${summary.success} document${summary.success === 1 ? '' : 's'} uploaded successfully`);
        onSuccess && onSuccess();
        setFiles([]);
        setName('');
        setDescription('');
        setDocumentType('general');
        setIsLocked(false);
        setAccessScope(committeeId ? 'committee' : 'board');
        setDownloadPolicy('download');
      } else {
        toast.error(`${summary.failed} document${summary.failed === 1 ? '' : 's'} failed to upload`);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0, currentFile: '' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>Add a new document to the board library</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File Drop Zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-200 rounded-xl p-10 text-center cursor-pointer hover:border-purple-300 transition-colors"
          >
            {files.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-4">
                  <FileText className="h-10 w-10 text-purple-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900 text-lg">
                      {files.length === 1 ? files[0].name : `${files.length} files selected`}
                    </p>
                    {files.length === 1 && (
                      <p className="text-sm text-gray-500">{formatFileSize(files[0].size)}</p>
                    )}
                  </div>
                </div>
                {files.length > 1 && (
                  <div className="max-h-28 overflow-y-auto text-sm text-gray-500">
                    {files.map((file) => (
                      <div key={file.name} className="truncate">{file.name}</div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2 text-lg">Click to select a file</p>
                <p className="text-sm text-gray-400">PDF, DOC, XLS, PPT, TXT, images, videos up to 100MB</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          <div className="space-y-2">
            <Label>Document Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={files.length > 1}
              placeholder="Enter document name"
            />
            {files.length > 1 && (
              <p className="text-xs text-gray-500">Document names will use each filename.</p>
            )}
          </div>

          {/* Folder Selection */}
          <div className="space-y-2">
            <Label>Save to Folder</Label>
            <FolderPicker
              open={open}
              selectedFolderId={selectedFolderId}
              onSelectFolderId={setSelectedFolderId}
              selectedLabel="Selected"
              initialFolderPath={currentFolderPath}
              committeeId={committeeId}
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Document Type</Label>
                <Select value={documentType} onValueChange={setDocumentType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="agenda">Agenda</SelectItem>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="resolution">Resolution</SelectItem>
                    <SelectItem value="report">Report</SelectItem>
                    <SelectItem value="policy">Policy</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Access Scope</Label>
                <Select value={accessScope} onValueChange={(v) => { setAccessScope(v); setIsLocked(v === 'executive_only'); }} disabled={!canManageSecurity}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {!committeeId && <SelectItem value="board">All Board Members</SelectItem>}
                    {committeeId && <SelectItem value="committee">Committee Members</SelectItem>}
                    {committeeId && <SelectItem value="board">Board Wide</SelectItem>}
                    <SelectItem value="executive_only">Executive Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Download Policy</Label>
                <Select value={downloadPolicy} onValueChange={setDownloadPolicy} disabled={!canManageSecurity}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="download">Allow Download</SelectItem>
                    <SelectItem value="preview_only">Preview Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {!canManageSecurity && (
              <p className="text-xs text-gray-500">Only admins or executive-level users can change secure document settings.</p>
            )}
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            disabled={uploading || files.length === 0}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Upload
          </Button>
        </div>

        {(uploadProgress.total > 0 || uploadQueue.length > 0 || uploadSummary) && (
          <div className="mt-4 space-y-2 text-sm">
            {uploadProgress.total > 0 && (
              <div className="text-gray-600">
                Uploading {uploadProgress.current} of {uploadProgress.total}
                {uploadProgress.currentFile ? ` • ${uploadProgress.currentFile}` : ''}
              </div>
            )}
            {uploadQueue.length > 0 && (
              <div className="space-y-1">
                {uploadQueue.map((item, idx) => (
                  <div key={`${item.name}-${idx}`} className="flex items-center justify-between text-gray-600">
                    <span className="truncate">{item.name}</span>
                    <span className="text-xs">
                      {item.status === 'pending' && 'Pending'}
                      {item.status === 'uploading' && 'Uploading'}
                      {item.status === 'success' && 'Uploaded'}
                      {item.status === 'failed' && 'Failed'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {uploadSummary && (
              <div className="text-gray-700">
                Uploaded {uploadSummary.success} of {uploadSummary.total}
                {uploadSummary.failed > 0 && ` • ${uploadSummary.failed} failed`}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// CREATE FOLDER DIALOG
// =============================================================================

const CreateFolderDialog = ({ open, onOpenChange, parentFolderId, onSuccess, committeeId }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#8B5CF6');
  const [loading, setLoading] = useState(false);

  const colors = ['#8B5CF6', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#EC4899', '#6B7280'];

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    setLoading(true);
    try {
      const url = committeeId ? `/board/documents/folders?committee_id=${committeeId}` : '/board/documents/folders';
      await api.post(url, {
        name,
        description,
        parent_folder_id: parentFolderId,
        color
      });
      onSuccess();
      setName('');
      setDescription('');
      setColor('#8B5CF6');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create folder');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Folder</DialogTitle>
          <DialogDescription>Organize your documents into folders</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Folder Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter folder name"
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2">
              {colors.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full transition-transform ${color === c ? 'scale-110 ring-2 ring-offset-2 ring-purple-500' : ''}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={loading || !name.trim()}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Create Folder
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// EDIT FOLDER DIALOG
// =============================================================================

const EditFolderDialog = ({ open, onOpenChange, folder, onSuccess, committeeId }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#8B5CF6');
  const [isLocked, setIsLocked] = useState(false);
  const [accessScope, setAccessScope] = useState(committeeId ? 'committee' : 'board');
  const [downloadPolicy, setDownloadPolicy] = useState('download');
  const [loading, setLoading] = useState(false);

  const colors = ['#8B5CF6', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#EC4899', '#6B7280'];

  useEffect(() => {
    if (folder) {
      setName(folder.name || '');
      setDescription(folder.description || '');
      setColor(folder.color || '#8B5CF6');
      setIsLocked(folder.is_locked || false);
    }
  }, [folder]);

  const handleUpdate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    setLoading(true);
    try {
      const url = committeeId
        ? `/board/documents/folders/${folder.id}?committee_id=${committeeId}`
        : `/board/documents/folders/${folder.id}`;
      await api.put(url, {
        name,
        description,
        color,
        is_locked: isLocked
      });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update folder');
    } finally {
      setLoading(false);
    }
  };

  if (!folder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Folder</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Folder Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter folder name"
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2">
              {colors.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full transition-transform ${color === c ? 'scale-110 ring-2 ring-offset-2 ring-purple-500' : ''}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isLocked"
              checked={isLocked}
              onChange={(e) => setIsLocked(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <Label htmlFor="isLocked" className="cursor-pointer">
              Lock folder (Executive Committee only)
            </Label>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleUpdate}
            disabled={loading || !name.trim()}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EditDocumentSecurityDialog = ({ open, onOpenChange, document, onSuccess, committeeId }) => {
  const [accessScope, setAccessScope] = useState(committeeId ? 'committee' : 'board');
  const [downloadPolicy, setDownloadPolicy] = useState('download');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (document) {
      setAccessScope(document.access_scope || (committeeId ? 'committee' : 'board'));
      setDownloadPolicy(document.download_policy || 'download');
    }
  }, [document, committeeId]);

  if (!document) return null;

  const handleSave = async () => {
    setLoading(true);
    try {
      const url = committeeId ? `/board/documents/${document.id}?committee_id=${committeeId}` : `/board/documents/${document.id}`;
      await api.put(url, { access_scope: accessScope, download_policy: downloadPolicy, is_locked: accessScope === 'executive_only' });
      toast.success('Document security updated');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update document security');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Document Security</DialogTitle>
          <DialogDescription>{document.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Access Scope</Label>
            <Select value={accessScope} onValueChange={setAccessScope}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {!committeeId && <SelectItem value="board">All Board Members</SelectItem>}
                {committeeId && <SelectItem value="committee">Committee Members</SelectItem>}
                {committeeId && <SelectItem value="board">Board Wide</SelectItem>}
                <SelectItem value="executive_only">Executive Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Download Policy</Label>
            <Select value={downloadPolicy} onValueChange={setDownloadPolicy}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="download">Allow Download</SelectItem>
                <SelectItem value="preview_only">Preview Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// MOVE DOCUMENT DIALOG
// =============================================================================

const MoveDocumentDialog = ({ open, onOpenChange, document: doc, onSuccess, committeeId }) => {
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [moving, setMoving] = useState(false);

  // Fetch folders when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedFolderId(null);
    }
  }, [open]);

  const handleMove = async () => {
    if (!doc) return;

    setMoving(true);
    try {
      const url = committeeId
        ? `/board/documents/${doc.id}/move?committee_id=${committeeId}`
        : `/board/documents/${doc.id}/move`;
      await api.patch(url, { target_folder_id: selectedFolderId ?? null });
      toast.success(`Document moved to ${selectedFolderId ? getCurrentFolderName() : 'Root'}`);
      onSuccess({ documentId: doc.id, folderId: selectedFolderId ?? null });
    } catch (err) {
      console.error('Move failed:', err.response?.data || err);
      if (err.response) {
        toast.error(err.response?.data?.detail || 'Failed to move document');
      } else {
        onSuccess({ documentId: doc.id, folderId: selectedFolderId ?? null });
      }
    } finally {
      setMoving(false);
    }
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Move Document</DialogTitle>
          <DialogDescription>
            Move &ldquo;{doc.name}&rdquo; to a different folder
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <FolderPicker
            open={open}
            selectedFolderId={selectedFolderId}
            onSelectFolderId={setSelectedFolderId}
            selectedLabel="Move to"
            excludeFolderIds={doc?.folder_id ? [doc.folder_id] : []}
            showTree
            committeeId={committeeId}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleMove}
            disabled={moving}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0"
          >
            {moving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FolderOpen className="h-4 w-4 mr-2" />}
            Move
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// MOVE FOLDER DIALOG
// =============================================================================

const MoveFolderDialog = ({ open, onOpenChange, folder, onSuccess, committeeId }) => {
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedFolderId(folder?.parent_folder_id || null);
    }
  }, [open, folder]);

  const handleMove = async () => {
    if (!folder) return;
    setMoving(true);
    try {
      const url = committeeId
        ? `/board/documents/folders/${folder.id}/move?committee_id=${committeeId}`
        : `/board/documents/folders/${folder.id}/move`;
      await api.patch(url, {
        parent_folder_id: selectedFolderId
      });
      toast.success('Folder moved successfully');
      onSuccess && onSuccess();
    } catch (err) {
      console.error('Move failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to move folder');
    } finally {
      setMoving(false);
    }
  };

  if (!folder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Move Folder</DialogTitle>
          <DialogDescription>
            Move &ldquo;{folder.name}&rdquo; to a different folder
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <FolderPicker
            open={open}
            selectedFolderId={selectedFolderId}
            onSelectFolderId={setSelectedFolderId}
            selectedLabel="Move to"
            excludeFolderIds={[folder.id]}
            blockFolderId={folder.id}
            showTree
            committeeId={committeeId}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleMove}
            disabled={moving}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0"
          >
            {moving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FolderOpen className="h-4 w-4 mr-2" />}
            Move
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// BOARD SETTINGS DIALOG
// =============================================================================

const BoardSettingsDialog = ({ open, onOpenChange, currentOrganizationId }) => {
  const [settings, setSettings] = useState(null);
  const [executiveCommittee, setExecutiveCommittee] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedMember, setSelectedMember] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [updatingVoting, setUpdatingVoting] = useState({});

  useEffect(() => {
    if (open) {
      fetchSettings();
      fetchBoardMembers();
    }
  }, [open, currentOrganizationId]);

  useEffect(() => {
    if (!currentOrganizationId) return;
    setSettings(null);
    setExecutiveCommittee([]);
    setBoardMembers([]);
  }, [currentOrganizationId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/board/documents/settings');
      setSettings(res.data.settings);
      setExecutiveCommittee(res.data.executive_committee || []);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchBoardMembers = async () => {
    try {
      const res = await api.get('/board/documents/members');
      setBoardMembers(res.data.members || []);
    } catch (err) {
      console.error('Failed to fetch board members:', err);
    }
  };

  const handleToggleVotingStatus = async (memberId, currentStatus) => {
    setUpdatingVoting(prev => ({ ...prev, [memberId]: true }));
    try {
      await api.put(`/board/documents/members/${memberId}/voting-status`, {
        is_voting_member: !currentStatus
      });
      // Update local state
      setBoardMembers(prev => prev.map(m =>
        m.id === memberId ? { ...m, is_voting_member: !currentStatus } : m
      ));
      toast.success(`Member marked as ${!currentStatus ? 'voting' : 'non-voting'}`);
    } catch (err) {
      toast.error('Failed to update voting status');
    } finally {
      setUpdatingVoting(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Only send fields that the backend accepts
      const settingsToSave = {
        max_file_size_mb: settings?.max_file_size_mb,
        allowed_file_types: settings?.allowed_file_types,
        notify_on_new_document: settings?.notify_on_new_document,
        notify_on_new_comment: settings?.notify_on_new_comment,
        notify_on_mention: settings?.notify_on_mention,
        default_document_access: settings?.default_document_access,
        quorum_type: settings?.quorum_type,
        quorum_value: settings?.quorum_value,
        quorum_count_only_voting: settings?.quorum_count_only_voting,
        meeting_start_roles: settings?.meeting_start_roles,
        meeting_executive_can_start: settings?.meeting_executive_can_start,
        // Agenda permissions
        agenda_edit_roles: settings?.agenda_edit_roles,
        agenda_edit_allow_executive: settings?.agenda_edit_allow_executive,
        agenda_edit_allow_admins: settings?.agenda_edit_allow_admins
      };
      await api.put('/board/documents/settings', settingsToSave);
      toast.success('Settings saved');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddExecutiveMember = async () => {
    if (!selectedMember) return;
    try {
      await api.post('/board/documents/settings/executive-committee', {
        user_id: selectedMember,
        position: selectedPosition || null
      });
      setSelectedMember('');
      setSelectedPosition('');
      fetchSettings();
      toast.success('Member added to Executive Committee');
    } catch (err) {
      toast.error('Failed to add member');
    }
  };

  const handleRemoveExecutiveMember = async (userId) => {
    try {
      await api.delete(`/board/documents/settings/executive-committee/${userId}`);
      fetchSettings();
      toast.success('Member removed from Executive Committee');
    } catch (err) {
      toast.error('Failed to remove member');
    }
  };

  const availableMembers = boardMembers.filter(
    m => !executiveCommittee.some(ec => ec.user_id === m.id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Governance Settings</DialogTitle>
          <DialogDescription>Configure board settings and executive committee</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="space-y-8 py-4">
            {/* Executive Committee Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Executive Committee
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Executive committee members have access to locked documents and folders.
              </p>

              {/* Current Members */}
              <div className="space-y-2 mb-4">
                {executiveCommittee.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">No executive committee members assigned</p>
                ) : (
                  executiveCommittee.map((member) => {
                    const positionLabels = {
                      chair: 'Chair',
                      vice_chair: 'Vice Chair',
                      secretary: 'Secretary',
                      treasurer: 'Treasurer',
                      president: 'President',
                      executive_director: 'Executive Director',
                      member_at_large: 'Member at Large'
                    };
                    const positionLabel = positionLabels[member.position] || member.position;

                    return (
                      <div key={member.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-sm font-medium text-purple-600">
                            {member.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{member.name}</p>
                            {member.position && (
                              <p className="text-xs text-purple-600 font-medium">{positionLabel}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveExecutiveMember(member.user_id)}
                        >
                          <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Add Member */}
              <div className="flex gap-2">
                <Select value={selectedMember} onValueChange={setSelectedMember}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select board member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedPosition} onValueChange={setSelectedPosition}>
                  <SelectTrigger className="w-44">
                    <SelectValue placeholder="Position..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chair">Chair</SelectItem>
                    <SelectItem value="vice_chair">Vice Chair</SelectItem>
                    <SelectItem value="secretary">Secretary</SelectItem>
                    <SelectItem value="treasurer">Treasurer</SelectItem>
                    <SelectItem value="president">President</SelectItem>
                    <SelectItem value="executive_director">Executive Director</SelectItem>
                    <SelectItem value="member_at_large">Member at Large</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleAddExecutiveMember} disabled={!selectedMember}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Board Members - Voting Status & Position */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Board Members
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Assign positions and toggle voting status for board members. Non-voting members (ex-officio) won't count toward quorum if "Count only voting members" is enabled.
              </p>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {boardMembers.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">No board members found</p>
                ) : (
                  boardMembers.map((member) => {
                    const positionLabels = {
                      chair: 'Chair',
                      vice_chair: 'Vice Chair',
                      secretary: 'Secretary',
                      treasurer: 'Treasurer',
                      president: 'President',
                      executive_director: 'Executive Director',
                      member_at_large: 'Member at Large'
                    };

                    return (
                      <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-sm font-medium text-purple-600">
                            {member.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{member.name}</p>
                            <div className="flex items-center gap-2">
                              <Select
                                value={member.position || 'none'}
                                onValueChange={async (value) => {
                                  try {
                                    await api.put(`/board/documents/members/${member.id}/position`, {
                                      position: value === 'none' ? null : value
                                    });
                                    setBoardMembers(prev => prev.map(m =>
                                      m.id === member.id ? { ...m, position: value === 'none' ? null : value } : m
                                    ));
                                    toast.success('Position updated');
                                  } catch (err) {
                                    toast.error('Failed to update position');
                                  }
                                }}
                              >
                                <SelectTrigger className="h-7 w-36 text-xs">
                                  <SelectValue placeholder="No position">
                                    {member.position ? positionLabels[member.position] || member.position : 'No position'}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No position</SelectItem>
                                  <SelectItem value="chair">Chair</SelectItem>
                                  <SelectItem value="vice_chair">Vice Chair</SelectItem>
                                  <SelectItem value="secretary">Secretary</SelectItem>
                                  <SelectItem value="treasurer">Treasurer</SelectItem>
                                  <SelectItem value="president">President</SelectItem>
                                  <SelectItem value="executive_director">Executive Director</SelectItem>
                                  <SelectItem value="member_at_large">Member at Large</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={member.is_voting_member ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                            {member.is_voting_member ? 'Voting' : 'Non-Voting'}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleVotingStatus(member.id, member.is_voting_member)}
                            disabled={updatingVoting[member.id]}
                          >
                            {updatingVoting[member.id] ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <span className="text-xs text-purple-600 hover:text-purple-800">
                                {member.is_voting_member ? 'Mark Non-Voting' : 'Mark Voting'}
                              </span>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Document Settings */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-600" />
                Document Settings
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Max File Size (MB)</Label>
                    <Input
                      type="number"
                      value={settings?.max_file_size_mb || 100}
                      onChange={(e) => setSettings({...settings, max_file_size_mb: parseInt(e.target.value)})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Default Access</Label>
                    <Select
                      value={settings?.default_document_access || 'board'}
                      onValueChange={(v) => setSettings({...settings, default_document_access: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="board">All Board Members</SelectItem>
                        <SelectItem value="executive_only">Executive Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Notifications</Label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings?.notify_on_new_document}
                        onChange={(e) => setSettings({...settings, notify_on_new_document: e.target.checked})}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">Notify on new document uploads</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings?.notify_on_new_comment}
                        onChange={(e) => setSettings({...settings, notify_on_new_comment: e.target.checked})}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">Notify on new comments</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings?.notify_on_mention}
                        onChange={(e) => setSettings({...settings, notify_on_mention: e.target.checked})}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">Notify on @mentions</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Meeting Control Settings */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                Meeting Controls
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Configure who can start and end meetings.
              </p>

              <div className="space-y-4">
                <div className="space-y-3">
                  <Label>Roles that can control meetings</Label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings?.meeting_start_roles?.includes('chair') ?? true}
                        onChange={(e) => {
                          const roles = settings?.meeting_start_roles || ['chair', 'secretary'];
                          if (e.target.checked) {
                            setSettings({...settings, meeting_start_roles: [...roles, 'chair']});
                          } else {
                            setSettings({...settings, meeting_start_roles: roles.filter(r => r !== 'chair')});
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">Chair</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings?.meeting_start_roles?.includes('vice_chair') ?? false}
                        onChange={(e) => {
                          const roles = settings?.meeting_start_roles || ['chair', 'secretary'];
                          if (e.target.checked) {
                            setSettings({...settings, meeting_start_roles: [...roles, 'vice_chair']});
                          } else {
                            setSettings({...settings, meeting_start_roles: roles.filter(r => r !== 'vice_chair')});
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">Vice Chair</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings?.meeting_start_roles?.includes('secretary') ?? true}
                        onChange={(e) => {
                          const roles = settings?.meeting_start_roles || ['chair', 'secretary'];
                          if (e.target.checked) {
                            setSettings({...settings, meeting_start_roles: [...roles, 'secretary']});
                          } else {
                            setSettings({...settings, meeting_start_roles: roles.filter(r => r !== 'secretary')});
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">Secretary</span>
                    </label>
                  </div>
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings?.meeting_executive_can_start ?? true}
                    onChange={(e) => setSettings({...settings, meeting_executive_can_start: e.target.checked})}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600"
                  />
                  <span className="text-sm">Executive Committee members can start/end meetings</span>
                </label>
              </div>
            </div>

            {/* Agenda Permissions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600" />
                Agenda Permissions
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Configure who can create, edit, and delete agenda items.
              </p>

              <div className="space-y-4">
                <div className="space-y-3">
                  <Label>Board positions that can edit agendas</Label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings?.agenda_edit_roles?.includes('chair') ?? true}
                        onChange={(e) => {
                          const roles = settings?.agenda_edit_roles || ['chair', 'secretary'];
                          if (e.target.checked) {
                            setSettings({...settings, agenda_edit_roles: [...new Set([...roles, 'chair'])]});
                          } else {
                            setSettings({...settings, agenda_edit_roles: roles.filter(r => r !== 'chair')});
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">Chair</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings?.agenda_edit_roles?.includes('vice_chair') ?? false}
                        onChange={(e) => {
                          const roles = settings?.agenda_edit_roles || ['chair', 'secretary'];
                          if (e.target.checked) {
                            setSettings({...settings, agenda_edit_roles: [...new Set([...roles, 'vice_chair'])]});
                          } else {
                            setSettings({...settings, agenda_edit_roles: roles.filter(r => r !== 'vice_chair')});
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">Vice Chair</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings?.agenda_edit_roles?.includes('secretary') ?? true}
                        onChange={(e) => {
                          const roles = settings?.agenda_edit_roles || ['chair', 'secretary'];
                          if (e.target.checked) {
                            setSettings({...settings, agenda_edit_roles: [...new Set([...roles, 'secretary'])]});
                          } else {
                            setSettings({...settings, agenda_edit_roles: roles.filter(r => r !== 'secretary')});
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">Secretary</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings?.agenda_edit_roles?.includes('treasurer') ?? false}
                        onChange={(e) => {
                          const roles = settings?.agenda_edit_roles || ['chair', 'secretary'];
                          if (e.target.checked) {
                            setSettings({...settings, agenda_edit_roles: [...new Set([...roles, 'treasurer'])]});
                          } else {
                            setSettings({...settings, agenda_edit_roles: roles.filter(r => r !== 'treasurer')});
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm">Treasurer</span>
                    </label>
                  </div>
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings?.agenda_edit_allow_executive ?? true}
                    onChange={(e) => setSettings({...settings, agenda_edit_allow_executive: e.target.checked})}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600"
                  />
                  <span className="text-sm">Executive Committee members can edit agendas</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings?.agenda_edit_allow_admins ?? true}
                    onChange={(e) => setSettings({...settings, agenda_edit_allow_admins: e.target.checked})}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600"
                  />
                  <span className="text-sm">Team Admins can edit agendas</span>
                </label>

                <p className="text-xs text-gray-400 italic">
                  Note: Team Owners always have full access to edit agendas.
                </p>
              </div>
            </div>

            {/* Quorum Settings */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Quorum Rules
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Configure how quorum is calculated based on your organization's bylaws.
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Quorum Calculation Method</Label>
                  <Select
                    value={settings?.quorum_type || 'majority'}
                    onValueChange={(v) => setSettings({...settings, quorum_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="majority">Majority (more than half)</SelectItem>
                      <SelectItem value="two_thirds">Two-Thirds (66.7%)</SelectItem>
                      <SelectItem value="percentage">Custom Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Number</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(settings?.quorum_type === 'percentage' || settings?.quorum_type === 'fixed') && (
                  <div className="space-y-2">
                    <Label>
                      {settings?.quorum_type === 'percentage' ? 'Required Percentage (%)' : 'Required Number of Members'}
                    </Label>
                    <Input
                      type="number"
                      min={settings?.quorum_type === 'percentage' ? 1 : 1}
                      max={settings?.quorum_type === 'percentage' ? 100 : undefined}
                      value={settings?.quorum_value || ''}
                      onChange={(e) => setSettings({...settings, quorum_value: parseInt(e.target.value) || null})}
                      placeholder={settings?.quorum_type === 'percentage' ? 'e.g., 60' : 'e.g., 5'}
                    />
                    {settings?.quorum_type === 'percentage' && (
                      <p className="text-xs text-gray-500">Enter a number between 1 and 100</p>
                    )}
                  </div>
                )}

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings?.quorum_count_only_voting ?? false}
                    onChange={(e) => setSettings({...settings, quorum_count_only_voting: e.target.checked})}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600"
                  />
                  <span className="text-sm">Count only voting members (excludes non-voting/ex-officio members)</span>
                </label>

                {/* Quorum Preview */}
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium text-purple-900">Current Rule:</p>
                  <p className="text-sm text-purple-700">
                    {settings?.quorum_type === 'majority' && 'More than half of board members must be present'}
                    {settings?.quorum_type === 'two_thirds' && 'At least two-thirds (66.7%) of board members must be present'}
                    {settings?.quorum_type === 'percentage' && settings?.quorum_value && `At least ${settings.quorum_value}% of board members must be present`}
                    {settings?.quorum_type === 'percentage' && !settings?.quorum_value && 'Please enter a percentage'}
                    {settings?.quorum_type === 'fixed' && settings?.quorum_value && `At least ${settings.quorum_value} board members must be present`}
                    {settings?.quorum_type === 'fixed' && !settings?.quorum_value && 'Please enter a number'}
                    {!settings?.quorum_type && 'Majority (default)'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 border-0"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save Settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { BoardSettingsDialog, UploadDocumentDialog };
export default DocumentsTab;
